import { api, BASE_URL, getToken } from './client';

export interface WorkspaceInfo {
  id: string;
  name: string;
  filePath: string;
  sourceLegacyFileHash: string | null;
  createdAt: string;
  lastOpenedAt: string;
  isActive: boolean;
  companyName: string | null;
  companyCode: string | null;
  /** When true, AI data egress is disabled (no data sent to external AI providers) */
  aiEgressDisabled: boolean;
}

export interface CreateWorkspaceRequest {
  name: string;
  companyName?: string;
  companyCode?: string;
}

export interface UpdateWorkspaceRequest {
  name?: string;
  companyName?: string;
  companyCode?: string;
  /** Set true to disable AI data egress for this workspace */
  aiEgressDisabled?: boolean;
}

export interface WorkspaceImportResult {
  success: boolean;
  workspaceId: string | null;
  workspaceName: string | null;
  totalRecordsImported: number;
  errorMessage: string | null;
  recordsPerTable: Record<string, number>;
}

export interface WorkspaceTablePreview {
  tableName: string;
  displayName: string;
  rowCount: number;
}

export interface WorkspaceImportPreview {
  fileName: string;
  fileSize: number;
  fileHash: string;
  alreadyImported: boolean;
  existingWorkspaceId: string | null;
  existingWorkspaceName: string | null;
  tables: WorkspaceTablePreview[];
}

const WORKSPACE_ID_KEY = 'a5core_workspace_id';

export function getWorkspaceId(): string | null {
  return localStorage.getItem(WORKSPACE_ID_KEY);
}

export function setWorkspaceId(id: string): void {
  localStorage.setItem(WORKSPACE_ID_KEY, id);
}

export function clearWorkspaceId(): void {
  localStorage.removeItem(WORKSPACE_ID_KEY);
}

export const workspacesApi = {
  // /workspaces is AdminOnly; /workspaces/mine returns the current user's
  // memberships (everything for Admins) and works for every role.
  list: () =>
    api.get<WorkspaceInfo[]>('/workspaces/mine'),

  get: (id: string) =>
    api.get<WorkspaceInfo>(`/workspaces/${id}`),

  create: (data: CreateWorkspaceRequest) =>
    api.post<WorkspaceInfo>('/workspaces', data),

  update: (id: string, data: UpdateWorkspaceRequest) =>
    api.put<WorkspaceInfo>(`/workspaces/${id}`, data),

  activate: (id: string) =>
    api.post<WorkspaceInfo>(`/workspaces/${id}/activate`),

  delete: (id: string) =>
    api.delete<void>(`/workspaces/${id}`),

  getActive: () =>
    api.get<WorkspaceInfo | null>('/workspaces/active'),

  previewImport: async (file: File): Promise<WorkspaceImportPreview> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.upload<WorkspaceImportPreview>('/workspaces/import/preview', formData);
  },

  importLegacy: async (file: File): Promise<WorkspaceImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.upload<WorkspaceImportResult>('/workspaces/import', formData);
  },

  exportUrl: (id: string) =>
    `${BASE_URL}/workspaces/${id}/export`,
};

/**
 * Build headers that include both JWT token and workspace ID.
 * Used for file download via fetch (since api.get doesn't support blob responses).
 */
export function workspaceFetchHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const workspaceId = getWorkspaceId();
  if (workspaceId) headers['X-Workspace-Id'] = workspaceId;
  return headers;
}
